import React, { ComponentType, memo, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { Image } from "@core-ui/components/image";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

import styles from "./GenericCrouton.scss";
import classNames from "classnames";
import { isEmpty } from "@core-ui/helpers/Utils";

//dls
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { IColors, ICornerRadius } from "@swiggy-private/dls-web-common/dist/components/types.internal";

//constants
import { CROUTON_TEST_IDS } from "./constants";

// widgets
import CroutonTextContent from "./TextContent";
import { CroutonTimer } from "@core-ui/components/crouton-timer";

// styles
import { StyledButton } from "./styles";

export interface GenericCroutonProps {
    title: string | ReactNode;
    subText?: string | ReactNode;
    leftIcon?: {
        icon?: ComponentType<unknown>;
        iconName?: IconName;
        strokeColor?: keyof IColors;
        fillColor?: keyof IColors;
    };
    icon?: string;
    iconHeight?: number;
    iconWidth?: number;
    preTextEl?: ReactNode;
    postTextEl?: ReactNode;
    durationOfCrouton?: number;
    postTextIcon?: string;
    cssProperties?: React.CSSProperties;
    closeIconCssProperties?: React.CSSProperties;
    wrapperClassName?: string;
    titleClassName?: string;
    subTextClassName?: string;
    buttonClassName?: string;
    iconClassName?: string;
    timerClassName?: string;
    onClick?: VoidFunction;
    impressionEvent?: VoidFunction;
    setCroutonVisibility?: (croutonVisibility: boolean) => void;
    onClose?: VoidFunction;
    closeIcon?: ReactNode;
    onCloseCrouton?: () => void;
    showTimer?: boolean;
    duration?: number;
    onCompleteTimer?: () => void;
    onTimerLoad?: () => void;
    precalculatedTimerDuration?: {
        minutes: number;
        seconds: number;
    };
    customWrapperDataTestId?: string;
    timerTextColor?: keyof IColors;
    textColor?: keyof IColors;
    timerKey?: string | number;
    backgroundColor?: keyof IColors;
    borderRadius?: keyof ICornerRadius;
    visuallyHidden?: boolean;
}

const ICON_SIZE = 14 * 2;

const POST_TEXT_ICON = {
    HEIGHT: 12 * 2,
    WIDTH: 10 * 2,
};

const GenericCrouton = ({
    title,
    subText = "",
    leftIcon,
    icon = "",
    iconHeight = ICON_SIZE,
    iconWidth = ICON_SIZE,
    preTextEl,
    postTextEl,
    durationOfCrouton = 0,
    postTextIcon = "",
    cssProperties = {},
    closeIconCssProperties = {},
    wrapperClassName = "",
    buttonClassName = "",
    iconClassName = "",
    titleClassName = "",
    subTextClassName = "",
    timerClassName = "",
    onClick,
    impressionEvent,
    setCroutonVisibility,
    onClose,
    closeIcon = null,
    onCloseCrouton,
    showTimer,
    duration,
    onCompleteTimer,
    onTimerLoad,
    precalculatedTimerDuration,
    customWrapperDataTestId = CROUTON_TEST_IDS.WRAPPER,
    timerTextColor,
    textColor,
    timerKey,
    backgroundColor,
    borderRadius,
    visuallyHidden = false,
}: GenericCroutonProps): ReactElement | null => {
    const [showCrouton, setShowCrouton] = useState(false);

    const allowTimer = useMemo(() => {
        if (!isEmpty(showTimer)) {
            if (!isEmpty(precalculatedTimerDuration)) {
                return true;
            }
            return !isEmpty(duration);
        }
        return false;
    }, [precalculatedTimerDuration, duration, showTimer]);

    const wrapperClass = classNames(wrapperClassName, styles.wrapper, {
        [styles.wrapperVisuallyHidden]: visuallyHidden,
    });
    const iconClass = classNames(styles.icon, iconClassName);

    const handleClose = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            e?.stopPropagation();
            setShowCrouton(false);
            onClose?.();
            onCloseCrouton?.();
        },
        [onClose, setShowCrouton, onCloseCrouton],
    );

    useEffect(() => {
        if (durationOfCrouton) {
            setTimeout(() => {
                handleClose();
            }, durationOfCrouton);
        }
    }, [durationOfCrouton, handleClose, onClose]);

    useEffect(() => {
        setShowCrouton(!isEmpty(title));
    }, [title]);

    useEffectOnce(() => {
        setCroutonVisibility?.(true);
        return () => {
            setCroutonVisibility?.(false);
        };
    });

    useEffect(() => {
        if (showCrouton) {
            impressionEvent?.();
        }
    }, [impressionEvent, showCrouton]);

    if (!showCrouton) return null;

    return (
        <>
            <div
                className={wrapperClass}
                data-testId={customWrapperDataTestId}
                aria-hidden={visuallyHidden ? "true" : undefined}
            >
                <StyledButton
                    bgColor={backgroundColor}
                    borderRadius={borderRadius}
                    className={buttonClassName}
                    style={cssProperties}
                    onClick={onClick}
                    data-testId={CROUTON_TEST_IDS.TITLE}
                >
                    {leftIcon?.icon && (
                        <GenericIcon
                            Icon={leftIcon.icon}
                            fillColor={leftIcon.fillColor}
                            strokeColor={leftIcon.strokeColor}
                        />
                    )}
                    {leftIcon?.iconName && (
                        <SpriteIconWeb
                            iconName={leftIcon.iconName}
                            fillColor={leftIcon.fillColor}
                            strokeColor={leftIcon.strokeColor}
                        />
                    )}
                    {icon && (
                        <Image key={icon} className={iconClass} imageId={icon} height={iconHeight} width={iconWidth} />
                    )}
                    {preTextEl && <>{preTextEl}</>}
                    <CroutonTextContent
                        title={title}
                        subText={subText}
                        titleClassName={titleClassName}
                        subTextClassName={subTextClassName}
                        textColor={textColor}
                    />
                    {postTextEl && <>{postTextEl}</>}
                    {postTextIcon && (
                        <Image
                            className={styles.postTextIcon}
                            imageId={postTextIcon}
                            height={POST_TEXT_ICON.HEIGHT}
                            width={POST_TEXT_ICON.WIDTH}
                        />
                    )}
                    {allowTimer && (
                        <CroutonTimer
                            key={timerKey}
                            duration={duration}
                            onComplete={onCompleteTimer}
                            onTimerLoad={onTimerLoad}
                            timerClassName={timerClassName}
                            precalculatedTimerDuration={precalculatedTimerDuration}
                            textColor={timerTextColor}
                        />
                    )}
                    {closeIcon && (
                        <button
                            style={closeIconCssProperties}
                            onClick={handleClose}
                            data-testid={CROUTON_TEST_IDS.CLOSE_BUTTON}
                        >
                            {closeIcon}
                        </button>
                    )}
                </StyledButton>
            </div>
        </>
    );
};

export default memo(GenericCrouton);
